import { STT_SAMPLE_RATE } from '@goaltree/backend/convex/lib/xaiRealtime'

// Brain dump dictation over one mic stream to xAI streaming speech-to-text.
// Web Speech on Android Chrome ends after short silences and beeps on every
// restart; a plain getUserMedia stream never does.

export type SttEvent = { text?: string; is_final?: boolean; speech_final?: boolean }

// Running transcript from /v1/stt events: `text` is cumulative within an
// utterance and starts over after `speech_final`; silence emits empty finals.
export function sttReducer() {
  let committed = ''
  let chunk = ''
  let current = ''
  return {
    push(e: SttEvent) {
      const raw = (e.text ?? '').trim()
      if (raw) {
        // If a "chunk final" ever restarts the text instead of extending it, keep the locked part.
        current = chunk && !raw.startsWith(chunk) && !chunk.startsWith(raw) ? join(chunk, raw) : raw
        if (e.is_final) chunk = current
      }
      if (e.is_final && e.speech_final) {
        committed = join(committed, current)
        chunk = ''
        current = ''
      }
      return join(committed, current)
    },
    text: () => join(committed, current),
  }
}

function join(a: string, b: string) {
  return [a.trim(), b.trim()].filter(Boolean).join(' ')
}

export function isStreamDictationSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof WebSocket !== 'undefined' &&
    !!(
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext
    )
  )
}

// Averaging decimator to 16 kHz PCM16, carrying the fractional position across buffers.
export function downsampler(from: number) {
  const ratio = from / STT_SAMPLE_RATE
  let t = 0
  return (input: Float32Array): ArrayBuffer => {
    const out = new Int16Array(Math.max(0, Math.ceil((input.length - t) / ratio)))
    let n = 0
    for (; t < input.length && n < out.length; t += ratio) {
      const i0 = Math.floor(t)
      const i1 = Math.min(input.length, Math.max(i0 + 1, Math.floor(t + ratio)))
      let sum = 0
      for (let i = i0; i < i1; i++) sum += input[i]
      const s = Math.max(-1, Math.min(1, sum / (i1 - i0)))
      out[n++] = s < 0 ? s * 0x8000 : s * 0x7fff
    }
    t -= input.length
    return out.buffer.slice(0, n * 2)
  }
}

export type DictationFailure =
  // Server path unusable (plan, config, socket refused): fall back to Web Speech.
  | { kind: 'unavailable'; error?: unknown }
  | { kind: 'mic' }
  // Worked, then the socket dropped mid-dump.
  | { kind: 'dropped' }

export type StreamDictation = {
  // Stop sending, flush the last words, release the mic; resolves with the final text.
  finish: () => Promise<string>
  close: () => void
}

const FLUSH_TIMEOUT_MS = 1500

export function startStreamDictation(opts: {
  connect: () => Promise<{ token: string; url: string }>
  onText: (text: string) => void
  onFail: (f: DictationFailure) => void
}): StreamDictation {
  const transcript = sttReducer()
  const AudioCtx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
  // Created synchronously so it starts inside the tap's user gesture (iOS).
  const ctx = new AudioCtx()
  void ctx.resume().catch(() => {})
  let ws: WebSocket | null = null
  let stream: MediaStream | null = null
  let processor: ScriptProcessorNode | null = null
  let ready = false
  let done = false
  let flushed: (() => void) | null = null
  const pending: ArrayBuffer[] = []

  function release() {
    processor?.disconnect()
    processor = null
    stream?.getTracks().forEach((t) => t.stop())
    stream = null
    void ctx.close().catch(() => {})
  }

  function close() {
    done = true
    release()
    if (ws && ws.readyState <= 1) ws.close()
    flushed?.()
  }

  function fail(f: DictationFailure) {
    if (done) return
    close()
    opts.onFail(f)
  }

  async function begin() {
    const [session, mic] = await Promise.allSettled([
      opts.connect(),
      navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      }),
    ])
    if (mic.status === 'fulfilled') {
      if (done) return mic.value.getTracks().forEach((t) => t.stop())
      stream = mic.value
    } else {
      const name = (mic.reason as { name?: string } | undefined)?.name
      return fail(
        name === 'NotAllowedError' || name === 'NotFoundError' || name === 'SecurityError'
          ? { kind: 'mic' }
          : { kind: 'unavailable', error: mic.reason },
      )
    }
    if (session.status === 'rejected') return fail({ kind: 'unavailable', error: session.reason })
    if (done) return
    // Auto-started dumps have no gesture of their own; a context that stays
    // suspended would stream nothing, so let Web Speech take over instead.
    if (ctx.state !== 'running')
      await Promise.race([ctx.resume().catch(() => {}), new Promise((r) => setTimeout(r, 1000))])
    if (done) return
    if (ctx.state !== 'running') return fail({ kind: 'unavailable', error: 'audio suspended' })

    const source = ctx.createMediaStreamSource(stream)
    const toPcm = downsampler(ctx.sampleRate)
    processor = ctx.createScriptProcessor(4096, 1, 1)
    const sink = ctx.createGain()
    sink.gain.value = 0
    processor.onaudioprocess = (e) => {
      if (done) return
      const pcm = toPcm(e.inputBuffer.getChannelData(0))
      if (ready && ws?.readyState === 1) ws.send(pcm)
      else pending.push(pcm)
    }
    source.connect(processor)
    processor.connect(sink)
    sink.connect(ctx.destination)

    const sock = new WebSocket(session.value.url, [`xai-client-secret.${session.value.token}`])
    ws = sock
    sock.onopen = () => {
      ready = true
      for (const pcm of pending.splice(0)) sock.send(pcm)
    }
    sock.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return
      let msg: SttEvent & { type?: string }
      try {
        msg = JSON.parse(ev.data)
      } catch {
        return
      }
      if (msg.type === 'transcript.partial') opts.onText(transcript.push(msg))
      else if (msg.type === 'transcript.done') flushed?.()
      else if (msg.type === 'error') console.error('[dictation] server error', msg)
    }
    sock.onclose = () => {
      if (done) return flushed?.()
      fail(ready ? { kind: 'dropped' } : { kind: 'unavailable' })
    }
  }

  void begin().catch((error) => fail({ kind: 'unavailable', error }))

  return {
    close,
    finish() {
      if (done) return Promise.resolve(transcript.text())
      done = true
      release()
      const sock = ws
      if (!sock || sock.readyState !== 1) {
        close()
        return Promise.resolve(transcript.text())
      }
      sock.send(JSON.stringify({ type: 'audio.done' }))
      return new Promise((resolve) => {
        const timer = setTimeout(() => flushed?.(), FLUSH_TIMEOUT_MS)
        flushed = () => {
          flushed = null
          clearTimeout(timer)
          if (sock.readyState <= 1) sock.close()
          resolve(transcript.text())
        }
      })
    },
  }
}
